import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiConsumer } from '../../service/api-consumer';
import { GeoFiltersRequest } from '../../interface/request/GeoFiltersRequest';
import { isGeoColumn } from '../../util/table-helper';
import { GeoClass, GeoFiltersData } from '../../interface/response/GeoFiltersResponse';
import { CASH_REGISTER_URL } from '../../util/url';
import { GeoColumns } from '../../enum/GeoDivisionColumnName';

/**
 * `filtersSlice` is a Redux slice responsible for managing the state of geo-based filters
 * in the application (e.g., continent → country → region → city).
 *
 * ### `fetchGeoFilters.fulfilled` reducer:
 * Triggered when geo filter data is successfully fetched from the API.
 *
 * #### General behavior:
 * - Sets `loading = false`
 * - Updates `geoFiltersData = action.payload`
 * - Initializes the following structures if they are undefined:
 *   - `allOptionsFull`
 *   - `allOptionsFiltered`
 *
 * #### Reads from `action.meta.arg`:
 * - `currentColumn`: The column currently being filtered
 * - `currentFilters`: Current selection state
 * - `previousFilters`: Filters before the fetch
 *
 * #### Determines if this is a deselection (unselect) action:
 * - By comparing selected IDs between `previousFilters` and `currentFilters`
 *
 * ---
 *
 * ### Per-column update logic:
 *
 * #### `allOptionsFull[column]`:
 * - Adds new options that are not already stored.
 *
 * #### `allOptionsFiltered[column]`:
 * Depends on the column’s position relative to `currentColumn` and whether it's a selection or deselection.
 *
 * - **Higher levels (above `currentColumn`)**:
 *   - Remain unchanged.
 *   - If missing, they are populated with the new incoming options.
 *
 * - **Current level (`currentColumn`)**:
 *   - Merges incoming options with the existing filtered ones.
 *
 * - **Lower levels (below `currentColumn`)**:
 *   - If **deselection**:
 *     - The immediate next level is reset to the new incoming options.
 *     - Deeper levels are filtered based on the incoming data.
 *   - If **selection**:
 *     - Incoming options are merged into existing filtered options.
 *
 *  * ### `resetGeoFilters` reducer:
 * Resets the current selection state of geo filters, while preserving cached option lists.
 *
 * #### Behavior:
 * - Clears `currentFilters` (no columns are selected)
 * - Clears `allOptionsFiltered` (filtered options for the current selection)
 * - Resets `geoFiltersData` to all-null structure
 * - **Does NOT reset `allOptionsFull`**, which keeps already loaded full option lists cached
 *
 * #### Purpose:
 * - Used when the UI needs to clear the selected filters without losing already fetched options
 * - Ensures that dependent dropdowns start fresh without making redundant API calls
 */

export const fetchGeoFilters = createAsyncThunk('filters/fetchGeoFilters', async (payload: GeoFiltersRequest) => {
  const response = await ApiConsumer.getGeoFilters(payload);
  return response;
});

interface GeoFiltersState {
  geoFiltersData: GeoFiltersData;
  currentFilters: Record<string, string | number | boolean>;
  allOptionsFiltered: Record<string, any>;
  allOptionsFull: Record<string, GeoClass[]>;
  loading: boolean;
  error: null | unknown;
}

const initialState: GeoFiltersState = {
  geoFiltersData: {
    Client: null,
    Company: null,
    Region: null,
    Group: null,
    Location: null,
    Device: null,
  },
  allOptionsFiltered: {},
  currentFilters: {},
  allOptionsFull: {},
  loading: false,
  error: null,
};

const filtersSlice = createSlice({
  name: 'geoFilters',
  initialState,
  reducers: {
    resetGeoFilters: (state) => {
      state.allOptionsFiltered = {};
      state.currentFilters = {};
      state.geoFiltersData = {
        Client: null,
        Company: null,
        Region: null,
        Group: null,
        Location: null,
        Device: null,
      };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchGeoFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchGeoFilters.fulfilled, (state, action) => {
        state.loading = false;

        const newData = action.payload;
        state.geoFiltersData = newData;

        if (!state.allOptionsFiltered) state.allOptionsFiltered = {};
        if (!state.allOptionsFull) state.allOptionsFull = {};

        const columns = Object.keys(state.geoFiltersData).filter((column: string) => isGeoColumn(column));

        const currentColumn = action.meta.arg.currentColumn || '';
        const currentFilters = action.meta.arg.currentFilters || {};
        const previousFilters = action.meta.arg.previousFilters || {};
        const currentPath = action.meta.arg.currentPath || '';

        state.currentFilters = currentFilters;

        const columnIndex = columns.indexOf(currentColumn);

        // IDs currently selected in the current column
        const currentIDs = (currentFilters[currentColumn] || []).map((f: GeoClass) => f.ID);

        // IDs previously selected in the current column (before fetching)
        const previousIDs = (previousFilters[currentColumn] || []).map((f: GeoClass) => f.ID);

        const isUnselect = previousIDs.length > currentIDs.length;

        for (const col of columns) {
          const incoming = newData[col] || [];

          const existingFull = state.allOptionsFull[col] || [];
          const mergedFull = [
            ...existingFull,
            ...incoming.filter((inc: GeoClass) => !existingFull.some((ex: GeoClass) => ex.ID === inc.ID)),
          ];
          state.allOptionsFull[col] = mergedFull;

          const colIndex = columns.indexOf(col);

          if (colIndex < columnIndex) {
            // Higher levels than the current one - don't change them.
            // If they don't have filtered options yet, set them to incoming.
            if (!state.allOptionsFiltered[col]) {
              state.allOptionsFiltered[col] = incoming;
            }
          } else if (colIndex === columnIndex) {
            // Current level - merge filtered items with new incoming ones
            const existingFiltered = state.allOptionsFiltered[col] || [];
            const mergedFiltered = [
              ...existingFiltered,
              ...incoming.filter((inc: GeoClass) => !existingFiltered.some((ex: GeoClass) => ex.ID === inc.ID)),
            ];
            state.allOptionsFiltered[col] = mergedFiltered;
          } else {
            if (isUnselect || (col === GeoColumns.LOCATION && currentPath === CASH_REGISTER_URL)) {
              if (colIndex === columnIndex + 1) {
                state.allOptionsFiltered[col] = incoming;
              } else {
                if (state.allOptionsFiltered[col]) {
                  // For deeper levels, filter existing options to only keep those
                  state.allOptionsFiltered[col] = state.allOptionsFiltered[col].filter((opt: GeoClass) =>
                    incoming.some((inc: GeoClass) => inc.ID === opt.ID),
                  );
                } else {
                  state.allOptionsFiltered[col] = incoming || [];
                }
              }
            } else {
              // If selecting - merge existing filtered options with incoming,
              const existingFiltered = state.allOptionsFiltered[col] || [];
              const mergedFiltered = [
                ...existingFiltered,
                ...incoming.filter((inc: GeoClass) => !existingFiltered.some((ex: GeoClass) => ex.ID === inc.ID)),
              ];
              state.allOptionsFiltered[col] = mergedFiltered;
            }
          }
        }
      })

      .addCase(fetchGeoFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      });
  },
});
export const { resetGeoFilters } = filtersSlice.actions;
export default filtersSlice.reducer;
